import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MarketplaceGate } from "@/components/marketplace/gate";
import { MarketplaceServiceCard } from "@/components/marketplace/service-card";
import { isFeatured } from "@/lib/marketplace/promotions";
import { fetchMarketplaceServices, type ServiceSort } from "@/lib/marketplace/queries";
import { buildPageMetadata } from "@/lib/seo";
import { useT } from "@/i18n";

export const Route = createFileRoute("/marketplace/explore")({
  validateSearch: (search: Record<string, unknown>) => ({
    q: typeof search["q"] === "string" ? (search["q"] as string) : undefined,
  }),
  head: () =>
    buildPageMetadata({
      path: "/marketplace/explore",
      title: "Explore creator services | BeHot.Now Marketplace",
      description:
        "Browse every creator, influencer and UGC service on BeHot.Now: fixed prices, verified audience data, protected payments and fast delivery.",
      keywords: ["creator services", "hire influencers", "UGC services", "buy promotion"],
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Marketplace", path: "/marketplace" },
        { name: "Explore", path: "/marketplace/explore" },
      ],
    }),
  component: ExplorePage,
});

function ExplorePage() {
  const { t } = useT();
  const { q } = Route.useSearch();
  const [search, setSearch] = useState(q ?? "");
  const [sort, setSort] = useState<ServiceSort>("hot");

  const { data, isLoading } = useQuery({
    queryKey: ["mp-explore", search, sort],
    queryFn: () => fetchMarketplaceServices({ search: search || undefined, sort, limit: 48 }),
  });

  return (
    <MarketplaceGate>
      <div className="mx-auto max-w-6xl px-4 py-8">
        <h1 className="font-display text-3xl font-bold">{t("mp.explore.title")}</h1>
        <div className="mt-5 flex flex-wrap gap-3">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("mp.explore.search_placeholder")}
            className="max-w-sm"
          />
          <Select value={sort} onValueChange={(v) => setSort(v as ServiceSort)}>
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="hot">{t("mp.sort.hot")}</SelectItem>
              <SelectItem value="new">{t("mp.sort.new")}</SelectItem>
              <SelectItem value="rating">{t("mp.sort.rating")}</SelectItem>
              <SelectItem value="fast">{t("mp.sort.fast")}</SelectItem>
              <SelectItem value="price_asc">{t("mp.sort.price_asc")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {(data ?? []).map((s) => (
            <MarketplaceServiceCard key={s.id} service={s} promoted={isFeatured(s.featured_until)} />
          ))}
        </div>
        {!isLoading && !data?.length && (
          <p className="mt-10 text-center text-sm text-muted-foreground">
            {t("mp.explore.empty")}
          </p>
        )}
      </div>
    </MarketplaceGate>
  );
}
